import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateWebsiteRequest,
  UpdateWebsiteRequest,
  WebsiteQuery,
} from '@troia-v3/types';
import { websitesApi } from '../services/websitesApi';

// ============================================================================
// QUERIES
// ============================================================================

export const useWebsites = (query?: WebsiteQuery) => {
  return useQuery({
    queryKey: ['websites', query],
    queryFn: () => websitesApi.getWebsites(query),
    staleTime: 5 * 60 * 1000,
  });
};

export const useWebsite = (id?: string) => {
  return useQuery({
    queryKey: ['websites', id],
    queryFn: () => websitesApi.getWebsiteById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// ============================================================================
// MUTATIONS
// ============================================================================

export const useCreateWebsite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWebsiteRequest) => websitesApi.createWebsite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websites'] });
    },
  });
};

export const useUpdateWebsite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWebsiteRequest }) =>
      websitesApi.updateWebsite(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['websites'] });
      queryClient.invalidateQueries({ queryKey: ['websites', variables.id] });
    },
  });
};

export const useDeleteWebsite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => websitesApi.deleteWebsite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websites'] });
    },
  });
};

export const usePublishWebsite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => websitesApi.publishWebsite(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['websites'] });
      queryClient.invalidateQueries({ queryKey: ['websites', id] });
    },
  });
};

export const useUnpublishWebsite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => websitesApi.unpublishWebsite(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['websites'] });
      queryClient.invalidateQueries({ queryKey: ['websites', id] });
    },
  });
};
